import { credentialSetOfConnection, type SocialConnection, type SocialCredentialSet } from '@mg/shared';

/**
 * Social Media Integrations on the web: the install's social developer apps, one credential set per
 * app (`SocialCredentialSet` in @mg/shared). A Tenant Admin adds their keys on Site Integrations; the
 * Connections page reads only whether each set has them.
 */

/**
 * Every social-app-keys query. Deliberately NOT under `['site-integrations']`: a settings save
 * invalidates that prefix, which must never refetch the key status of every open Connections page.
 */
export const SOCIAL_APP_KEYS_QUERY_KEY = ['social-app-keys'] as const;
/** The Tenant Admin's view of every set (Site Integrations). */
export const SOCIAL_APP_KEYS_VIEW_KEY = ['social-app-keys', 'view'] as const;
/** Whether each set has both values (the Connections page's gating; any signed-in user). */
export const SOCIAL_APP_KEYS_STATUS_KEY = ['social-app-keys', 'status'] as const;

/** The id of the Social Media Integrations card on Site Integrations. */
export const SOCIAL_APPS_ANCHOR = 'social-media-integrations';
/**
 * Where "Open Site Integrations" goes: straight to the card. Never with `?ws=`: Site Integrations
 * is install-level, where the workspace context strips a `ws` from the address - and the hash with it.
 */
export const SOCIAL_APPS_LINK = `/site-integrations#${SOCIAL_APPS_ANCHOR}`;

/** Scroll the Social Media Integrations card into view, when it is on the page already. */
export function scrollToSocialApps(): void {
  document.getElementById(SOCIAL_APPS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * The set an existing connection's Reconnect - and its "Turn on" / "Enable threads" - signs in with;
 * null when it keeps its own stored app (Instagram Login) or has no reconnect flow.
 *
 * The server says so on each connection (`reconnectCredentialSet`, worked out by the same function
 * its reconnect uses). An API older than that field leaves it out, and then the same shared function
 * works it out here - absent is not null: null means "no set", and would wrongly never gate.
 */
export function reconnectSetOf(
  conn: Pick<SocialConnection, 'platform' | 'scopes' | 'reconnectCredentialSet'>,
): SocialCredentialSet | null {
  return conn.reconnectCredentialSet !== undefined
    ? conn.reconnectCredentialSet
    : credentialSetOfConnection(conn.platform, conn.scopes);
}
